import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useDocsetManifest, useNodeByPath} from '../context/DocsetContext';
import {SearchProvider} from '../context/SearchContext';
import {findFirstContentNode} from '../utils/navigation';
import {resolveResource} from '../utils/resources';
import type {DocNode} from '../types/docset';
import {DocsetLayout} from '../ui/layout/DocsetLayout';
import type {ContentState} from '../types/content';
import type {DocContentPayload} from '../types/docset';

type CacheEntry = {version: string; payload: DocContentPayload};

const contentCache = new Map<string, CacheEntry>();

export function DocsetRoute() {
  const manifest = useDocsetManifest();
  const navigate = useNavigate();
  const params = useParams();
  const restPath = params['*'] ?? '';
  const pathSegments = restPath.split('/').filter(Boolean);

  const fallbackNode = useMemo(() => findFirstContentNode(manifest.nav), [manifest]);
  const node = useNodeByPath(pathSegments) ?? fallbackNode;
  const [contentState, setContentState] = useState<ContentState>({status: 'idle'});

  useEffect(() => {
    const title = node?.title ? `${node.title} · ${manifest.title}` : manifest.title;
    document.title = title;
  }, [node, manifest.title]);

  useEffect(() => {
    if (contentState.status === 'ready') {
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  }, [contentState.status, node?.pathSegments.join('/')]);

  useEffect(() => {
    if (!restPath && fallbackNode) {
      const target = fallbackNode.pathSegments.join('/');
      if (target) {
        navigate(target, {replace: true});
      }
    }
  }, [fallbackNode, navigate, restPath]);

  useEffect(() => {
    if (!node?.contentHref) {
      setContentState({status: 'error', error: new Error('Document not found')});
      return;
    }

    const cacheKey = `${manifest.id}:${node.pathSegments.join('/')}`;
    const cached = contentCache.get(cacheKey);
    if (cached && cached.version === manifest.version) {
      setContentState({status: 'ready', payload: cached.payload});
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    async function loadContent(target: DocNode) {
      setContentState({status: 'loading'});
      try {
        const resourceUrl = resolveResource(manifest, target.contentHref ?? '');
        const response = await fetch(`${resourceUrl}?v=${manifest.version}`, {
          signal: controller.signal,
          cache: 'no-store',
        });
        if (!response.ok) {
          throw new Error(`Unable to load document (${response.status})`);
        }
        const payload = (await response.json()) as DocContentPayload;
        contentCache.set(cacheKey, {version: manifest.version, payload});
        if (!cancelled) {
          setContentState({status: 'ready', payload});
        }
      } catch (error) {
        if (!cancelled) {
          setContentState({
            status: 'error',
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
      }
    }

    loadContent(node);

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [manifest, node]);

  useEffect(() => {
    // Invalidate cache when manifest version changes
    for (const [key, entry] of contentCache.entries()) {
      if (!key.startsWith(`${manifest.id}:`) || entry.version !== manifest.version) {
        contentCache.delete(key);
      }
    }
  }, [manifest.version]);

  return (
    <SearchProvider>
      <DocsetLayout activeNode={node} contentState={contentState} />
    </SearchProvider>
  );
}
